import React, { FC, useMemo } from 'react';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import { Box } from '@mui/material';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { useQuery } from '@apollo/client';
import { GET_ALL_PRODUCTS } from 'graphql/secure/queries';
import { Plus } from '@styled-icons/bootstrap/Plus';
import useConfig from 'hooks/useConfig';
import usePageTitle from 'hooks/usePageTitle';
import useProductActions from 'FeatureModules/ProductPage/hooks/useProductActions';
import ActiveProductTable from 'FeatureModules/ProductPage/Components/Tables/ActiveProductTable';
import ArchivedProductTable from 'FeatureModules/ProductPage/Components/Tables/ArchivedProductTable';
import ProductSummarySkeleton from 'FeatureModules/ProductPage/Components/Tables/ProductSummarySkeleton';
import { styled } from '@mui/material/styles';
import { EmptyStateCard, CardIllustration } from 'DesignSystem';
import {
  EBackgroundTypes,
  EItemTypes,
  EIllustrationTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { PRODUCTS_PER_PAGE } from 'utils/pagination.utils';
import { IGraphQlConnection } from 'types/core.types';
import { removeGraphConnections } from 'utils/graphql.utils';
import { IProduct } from '../types/product.types';
import { TSortOrder } from 'types/pagination.types';
import EmptySearchResults from 'Components/EmptySearchResults';
import PageContainer from 'Components/PageContainer';
import ActionContainer from 'Components/ActionContainer';
import ErrorState from 'Components/ErrorState';

interface IProductsProps {
  isArchived?: boolean;
}

const Container = styled('div')(() => ({
  position: 'relative',
  minHeight: 50,
  marginTop: 16,

  '& .MuiGrid-item': {
    display: 'flex',
  },
}));

const Products: FC<IProductsProps> = ({ isArchived }) => {
  usePageTitle('Product pages');
  const { handleCreateProduct } = useProductActions();
  const { appQueryParams } = useConfig();
  const [query, setQuery] = useQueryParams({
    [appQueryParams.order]: withDefault(StringParam, 'DESC'),
    [appQueryParams.orderBy]: withDefault(StringParam, ''),
    [appQueryParams.query]: withDefault(StringParam, ''),
  });

  const searchTerm = query[appQueryParams.query];
  const orderByField = query[appQueryParams.orderBy];
  const orderByMethod = query[appQueryParams.order] as TSortOrder | undefined;

  const { data, loading, error, fetchMore } = useQuery<{
    products: IGraphQlConnection<IProduct>;
  }>(GET_ALL_PRODUCTS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      first: PRODUCTS_PER_PAGE,
      filters: {
        ...(searchTerm && { searchTerm }),
        status: isArchived ? ['ARCHIVED'] : ['DRAFT', 'PUBLISHED'],
      },
      orderBy: {
        ...(orderByField && { orderByField }),
        ...(orderByMethod && { orderByMethod }),
      },
    },
  });

  const hasNextPage: boolean = !!data?.products?.pageInfo?.hasNextPage;
  const endCursor = data?.products?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setQuery({ [appQueryParams.query]: searchTerm });
  };

  const products = useMemo(
    () => (data?.products ? removeGraphConnections(data.products) : []),
    [data]
  );

  if (error) return <ErrorState />;

  if (!products?.length && !searchTerm?.length) {
    return (
      <PageContainer>
        <Box mt={4}>
          <EmptyStateCard
            title="Create your first product to start telling the story of your supply chains"
            onCreateItemClick={handleCreateProduct}
            variant={EItemTypes.PRODUCT}
            buttonText="Add product"
            background={EBackgroundTypes.WORLD}
            size={EAvailableSizes.LARGE}
            data-testid="product-getting-started-card"
            renderIllustration={
              <CardIllustration
                image={EIllustrationTypes.LOCATIONS}
                itemType="LOCATIONS"
                width={300}
                right={25}
                top={0}
              />
            }
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <ActionContainer>
          <SearchToolbar
            delay={500}
            setSearchTerm={handleSearchTermChange}
            searchTerm={searchTerm || ''}
            className="search-input"
            data-testid="product-search-input"
            placeholder="Search product"
            actions={
              <Box ml={1}>
                <ThemeButton
                  size="medium"
                  color="PRIMARY"
                  onClick={handleCreateProduct}
                  startIcon={<Plus size={20} color="white" />}
                  data-testid="create-product-btn"
                >
                  Add new product
                </ThemeButton>
              </Box>
            }
          />
        </ActionContainer>
        {loading ? (
          <Box position="relative" mt={16}>
            <Loader />
          </Box>
        ) : !isArchived ? (
          <ActiveProductTable
            products={products}
            hasNextPage={hasNextPage}
            onPageEndReached={handlePageEndReached}
          />
        ) : (
          <ArchivedProductTable
            products={products}
            hasNextPage={hasNextPage}
            onPageEndReached={handlePageEndReached}
          />
        )}
      </Container>

      {!products?.length && !!searchTerm?.length && !loading ? (
        <EmptySearchResults
          message="No search results found"
          action={{
            label: 'Create draft',
            onClick: handleCreateProduct,
          }}
          skeletonComponent={<ProductSummarySkeleton title={searchTerm} />}
        />
      ) : undefined}
    </PageContainer>
  );
};

export default Products;
